<template>
  <se-modal
    :active="signInModalActive"
    :disable-close="loggingIn || requestingPasswordReset"
    :title="!passwordResetActive ? 'loyalty.signIn' : 'loyalty.resetPassword'"
    class="text-dark-gray"
    max-width="max-w-sm"
    @keyup.enter="!passwordResetActive ? initSubmitLoyaltyForm() : initSubmitPasswordReset()"
    @close="toggleLoginModal(false)"
  >
    <transition name="slide-fade">
      <se-alert v-if="errorMessage" type="danger" class="mb-3">
        <template #desc>
          <p class="text-sm sm:text-base">{{ errorMessage }}</p>
        </template>
      </se-alert>
    </transition>
    <transition name="slide-fade" mode="out-in">
      <div v-if="!passwordResetActive" class="flex flex-col gap-3">
        <transition name="slide-fade">
          <se-alert v-if="confirmationMessage" type="info">
            <template #desc>
              <p class="text-sm sm:text-base">{{ confirmationMessage }}</p>
            </template>
          </se-alert>
        </transition>
        <div>{{ $t('loyalty.signInEnterDetails') }}</div>
        <form-kit id="loyalty-login-form" type="form" :disabled="loggingIn" :actions="false" @submit="(data) => loginSubmitHandler(data)">
          <form-kit-schema :schema="LoyaltyLoginForm" :data="data" />
        </form-kit>

        <div class="flex items-center justify-between gap-x-3 sm:justify-end">
          <se-button class="sm:w-fit" :disabled="loggingIn" :spinner="loggingIn" @click="initSubmitLoyaltyForm()">
            {{ $t('loyalty.signIn') }}
          </se-button>
          <se-button class="sm:w-fit" color="gray" :disabled="loggingIn" @click="toggleLoginModal(false)">
            {{ $t('shared.cancel') }}
          </se-button>
        </div>
      </div>
      <div v-else class="flex flex-col gap-3">
        <p>An email with a link to reset your password will be sent to your email address.</p>
        <form-kit
          id="loyalty-login-password-reset"
          type="form"
          :disabled="requestingPasswordReset"
          :actions="false"
          @submit="(data) => resetPasswordSubmitHandler(data)"
        >
          <form-kit-schema :schema="LoyaltyLoginPasswordReset" :data="data" />
        </form-kit>
        <se-button
          class="w-full"
          :disabled="requestingPasswordReset"
          :spinner="requestingPasswordReset"
          @click="initSubmitPasswordReset()"
        >
          {{ $t('loyalty.resetPassword') }}
        </se-button>
        <p
          :class="requestingPasswordReset ? 'text-light-gray' : 'text-info cursor-pointer hover:underline'"
          class="ml-auto mr-auto text-sm"
          @click="requestingPasswordReset ? null : togglePasswordReset(false)"
        >
          Back to Login
        </p>
      </div>
    </transition>
  </se-modal>
</template>

<script setup lang="ts">
import { FormKit, FormKitSchema, submitForm } from '@formkit/vue';
import LoyaltyLoginForm from '@f/schema/loyalty-login-form';
import LoyaltyLoginPasswordReset from '@f/schema/loyalty-login-password-reset';
import { useLoyaltyStore } from '@f/store/loyaltyStore';

const loginModalActive = ref(false);
const loggingIn = ref(false);
const requestingPasswordReset = ref(false);
const passwordResetActive = ref(false);
const errorMessage = ref('');
const confirmationMessage = ref('');
const loyaltyStore = useLoyaltyStore();
const { signInModalActive } = storeToRefs(loyaltyStore);

const clearError = () => {
  errorMessage.value = '';
};

const toggleLoginModal = (active: boolean) => {
  signInModalActive.value = active;
  passwordResetActive.value = false;
  confirmationMessage.value = '';
  clearError();
};

const initSubmitLoyaltyForm = () => {
  submitForm('loyalty-login-form');
};

const initSubmitPasswordReset = () => {
  submitForm('loyalty-login-password-reset');
};

const loginSubmitHandler = async (formData: { email: string; password: string }) => {
  try {
    clearError();
    loggingIn.value = true;
    await loyaltyStore.login(formData);
    toggleLoginModal(false);
  } catch (error: any) {
    errorMessage.value = error.message;
  } finally {
    loggingIn.value = false;
  }
};

const togglePasswordReset = (active: boolean) => {
  passwordResetActive.value = active;
  confirmationMessage.value = '';
};

const showPasswordRest = () => {
  togglePasswordReset(true);
};

const resetPasswordSubmitHandler = async (formData: { email: string }) => {
  try {
    clearError();
    requestingPasswordReset.value = true;
    await loyaltyStore.requestPasswordReset(formData.email);
    togglePasswordReset(false);
    confirmationMessage.value = 'A password reset email has been sent.';
  } catch (error: any) {
    errorMessage.value = error.message;
  } finally {
    requestingPasswordReset.value = false;
  }
};

const data = reactive({
  showPasswordRest,
});
</script>
